// CANVAS-CORE + LIVE-EVENTS (docs/design/2026-10-31-consolidate-then-demo.md A5, A14, §6.2): the
// agentic Live canvas. Blocks are existing screens (board, calendar, doc, image grid, KPI tile,
// chain, transcript) placed by an agent's `show_user` (MCP), the voice front, or the viewer; each
// is served only to a viewer who may read its subject (a block the viewer may not see is left
// out, never shown empty). The live channel carries `run.step`, `task.changed`,
// `helper.commitment` and `canvas.op` for one Live session, replays what a reconnect missed, and
// the screens keep polling as the fallback. Behind the `live_canvas` provisioning switch.
// services/api/src/canvas/ops.ts re-exports the op shapes for the voice service.
import { z } from "zod";
import { CanvasBlockKind, CanvasInteraction, LiveView } from "../enums";
import { IsoDate, Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { ChangePing } from "./live";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

/** A block's id on the canvas: chosen by whoever shows it, reused to arrange, highlight, clear. */
export const CanvasBlockId = z
  .string()
  .max(40)
  .regex(/^[a-z0-9][a-z0-9_-]{0,39}$/)
  .meta({ id: "CanvasBlockId" });

/** What a block shows: the subject's id (which one each kind needs is checked on the block). */
export const CanvasBlockRef = z
  .strictObject({
    board_id: Uuid.optional(),
    task_id: Uuid.optional(),
    seat_id: Uuid.optional(),
    page_id: Uuid.optional(),
    thread_id: Uuid.optional(),
    calendar_id: Uuid.optional(),
    /** Calendar: the day shown (company time); default today. */
    date: IsoDate.optional(),
    /** CANVAS-BLOCKS: calendar, the range around `date` (default day). */
    calendar_view: z.enum(["day", "week", "month"]).optional(),
    /** CANVAS-BLOCKS: image and video, the file (read through the authenticated file proxy). */
    file_id: Uuid.optional(),
  })
  .meta({ id: "CanvasBlockRef" });

/** A board block's filter ("today's cards"); the board block applies it under the viewer. */
export const CanvasBlockFilter = z
  .strictObject({
    due: z.enum(["today", "this_week", "overdue"]).optional(),
    assignee_membership_id: Uuid.optional(),
    /** The column (the card's status). */
    status: z.string().min(1).max(60).optional(),
    query: z.string().min(1).max(200).optional(),
    /** CANVAS-BLOCKS: the board's columns (default) or its list. */
    view: z.enum(["board", "list"]).optional(),
  })
  .meta({ id: "CanvasBlockFilter" });

/** The ref each kind needs (calendar needs none: the company's calendar, today). */
export const CANVAS_BLOCK_REF: Record<
  z.infer<typeof CanvasBlockKind>,
  keyof z.infer<typeof CanvasBlockRef> | null
> = {
  board: "board_id",
  calendar: null,
  doc: "page_id",
  image_grid: "task_id",
  kpi: "seat_id",
  chain: "task_id",
  transcript: "thread_id",
  image: "file_id",
  video: "file_id",
};

export const CanvasBlock = z
  .strictObject({
    id: CanvasBlockId,
    kind: CanvasBlockKind,
    title: z.string().min(1).max(120).optional(),
    ref: CanvasBlockRef.default({}),
    filter: CanvasBlockFilter.optional(),
  })
  .superRefine((b, ctx) => {
    const need = CANVAS_BLOCK_REF[b.kind];
    if (need && !b.ref[need])
      ctx.addIssue({ code: "custom", path: ["ref", need], message: `${b.kind} needs ref.${need}` });
    if (b.filter && b.kind !== "board")
      ctx.addIssue({ code: "custom", path: ["filter"], message: "only a board block filters" });
  })
  .meta({ id: "CanvasBlock" });
export type CanvasBlock = z.infer<typeof CanvasBlock>;

/**
 * LIVE-DESKTOP: how the desk lays out its open, unpinned windows: `tile` (2 = halves, 3 = big
 * left + two right, 4 = quadrants, more = rows of three), `focus` (the first takes 70%, the
 * rest a right column), `cascade`, `stack`. `grid` is the older name of `tile`.
 */
export const CanvasLayout = z
  .enum(["grid", "focus", "tile", "cascade", "stack"])
  .meta({ id: "CanvasLayout" });

/** LIVE-DESKTOP: a window's state on the desk. */
export const CanvasWindowState = z
  .enum(["open", "docked", "max", "pinned"])
  .meta({ id: "CanvasWindowState" });

/**
 * LIVE-DESKTOP: a window's place on the desk, in units of a 24 x 16 grid of the surface (so it
 * scales with the screen); `z` is the stacking order (higher is in front).
 */
export const CanvasWindow = z
  .strictObject({
    id: CanvasBlockId,
    x: z.number().int().min(0).max(23),
    y: z.number().int().min(0).max(15),
    w: z.number().int().min(1).max(24),
    h: z.number().int().min(1).max(16),
    z: z.number().int().min(0).max(1_000_000).optional(),
    state: CanvasWindowState.optional(),
  })
  .meta({ id: "CanvasWindow" });
export type CanvasWindow = z.infer<typeof CanvasWindow>;

/** `show_user`'s four ops. */
export const CanvasShowOp = z.strictObject({
  op: z.literal("show"),
  blocks: z.array(CanvasBlock).min(1).max(8),
  /** true: the shown blocks replace the canvas; false (default): added, or updated by id. */
  replace: z.boolean().optional(),
});
export const CanvasArrangeOp = z.strictObject({
  op: z.literal("arrange"),
  order: z.array(CanvasBlockId).min(1).max(16),
  layout: CanvasLayout.optional(),
  /** LIVE-DESKTOP: the window the layout is built around (focus: the big one); first in order. */
  anchor: CanvasBlockId.optional(),
});
export const CanvasHighlightOp = z.strictObject({
  op: z.literal("highlight"),
  block_id: CanvasBlockId,
  /** The card, event or file inside the block to point at. */
  item_id: Uuid.optional(),
  note: z.string().min(1).max(200).optional(),
  /** LIVE-DESKTOP: dim the other windows (default true) until Esc or the next highlight. */
  dim_others: z.boolean().optional(),
});
/** LIVE-DESKTOP: put windows away in the dock (not removed); omitted: every unpinned window. */
export const CanvasTidyOp = z.strictObject({
  op: z.literal("tidy"),
  block_ids: z.array(CanvasBlockId).min(1).max(16).optional(),
});
/**
 * LIVE-DESKTOP: windows' geometry upserted (units of the 24 x 16 grid). Written by the page when
 * the person moves, resizes, docks, pins or fronts a window, and by agents; an agent never moves
 * a pinned window, and the page asks first when the person touched it in the last 5 s.
 */
export const CanvasPlaceOp = z.strictObject({
  op: z.literal("place"),
  windows: z.array(CanvasWindow).min(1).max(16),
});
export const CanvasClearOp = z.strictObject({
  op: z.literal("clear"),
  /** Omitted: the whole canvas. */
  block_ids: z.array(CanvasBlockId).min(1).max(16).optional(),
});
/**
 * CANVAS-BLOCKS: the agent switches the person's Live view (to Browser or Computer for a login
 * hand-off, a CAPTCHA, a confirm; back to Workspace when done). `session` is the screen lease
 * to open there. The page never takes control mid-click: a person who interacted in the last
 * few seconds is asked instead.
 */
export const CanvasFocusViewOp = z.strictObject({
  op: z.literal("focus_view"),
  view: LiveView,
  session: Uuid.optional(),
  tab: z.string().min(1).max(80).optional(),
  reason: z.string().min(1).max(200).optional(),
});
/**
 * CANVAS-BLOCKS: what a person did on the Live page ("opened card X"), so the agent reads the
 * clicks as context beside what was said. Written by the page (`liveCanvas.interact`), never by
 * an agent; folding ignores it.
 */
export const CanvasInteractOp = z.strictObject({
  op: z.literal("interact"),
  action: CanvasInteraction,
  block_id: CanvasBlockId.optional(),
  item_id: Uuid.optional(),
  /** A short note: the card's title, the view switched to, the column moved into. */
  detail: z.string().min(1).max(200).optional(),
});
export const CanvasOp = z
  .discriminatedUnion("op", [
    CanvasShowOp,
    CanvasArrangeOp,
    CanvasHighlightOp,
    CanvasClearOp,
    CanvasFocusViewOp,
    CanvasInteractOp,
    CanvasTidyOp,
    CanvasPlaceOp,
  ])
  .meta({ id: "CanvasOp" });
export type CanvasOp = z.infer<typeof CanvasOp>;

export const CanvasHighlight = z
  .object({
    block_id: CanvasBlockId,
    item_id: Uuid.nullable(),
    note: z.string().nullable(),
    /** LIVE-DESKTOP: the others dimmed (spotlight). */
    dim_others: z.boolean().optional(),
  })
  .meta({ id: "CanvasHighlight" });

/** LIVE-DESKTOP: a window's folded geometry (a docked window never placed has w = h = 0). */
export const CanvasWindowGeometry = z
  .object({
    x: z.number().int(),
    y: z.number().int(),
    w: z.number().int(),
    h: z.number().int(),
    z: z.number().int(),
    state: CanvasWindowState,
  })
  .meta({ id: "CanvasWindowGeometry" });

/**
 * A viewer's canvas: the fold of the ops it may see (its session's and the company-wide ones),
 * each block re-checked against the viewer. `cursor` is the live stream's resume point.
 */
export const CanvasState = z
  .object({
    session_id: Uuid,
    blocks: z.array(CanvasBlock),
    layout: CanvasLayout,
    highlight: CanvasHighlight.nullable(),
    cursor: z.string(),
    updated_at: Timestamp.nullable(),
    /** LIVE-DESKTOP: the desk's geometry by block id (blocks without a place are auto-placed). */
    windows: z.record(z.string(), CanvasWindowGeometry).optional(),
    /** LIVE-DESKTOP: block ids front first. */
    front: z.array(CanvasBlockId).optional(),
  })
  .meta({ id: "CanvasState" });
export type CanvasState = z.infer<typeof CanvasState>;

export const LiveCanvasQuery = z.object({
  /** Omitted: a new session id is minted (the browser keeps it for the tab). */
  session_id: Uuid.optional(),
});

export const LiveCanvasEventsQuery = z.object({
  session_id: Uuid,
  /** The resume point (`cursor`, or the last frame's `id`); `Last-Event-ID` wins when sent. */
  last_event_id: z.string().max(200).optional(),
  max_seconds: z.coerce.number().int().min(0).max(3600).optional(),
});

/** What `show_user` writes: one op, for one Live session or (omitted) every Live viewer. */
export const CanvasOpRequest = z.strictObject({
  session_id: Uuid.optional(),
  op: CanvasOp,
});
export type CanvasOpRequest = z.infer<typeof CanvasOpRequest>;

export const CanvasOpResult = z.object({
  id: Uuid,
  /** `session`: one Live session; `company`: every Live viewer (each sees what it may). */
  target: z.enum(["session", "company"]),
});
export type CanvasOpResult = z.infer<typeof CanvasOpResult>;

/**
 * CANVAS-BLOCKS: a person's interaction, for its Live session. LIVE-DESKTOP widened it to the
 * desk ops the page writes for its own session: `place` (geometry), `tidy` (dock) and `clear`
 * with block ids (a window the person closed).
 */
export const CanvasInteractRequest = z
  .strictObject({
    session_id: Uuid,
    op: z.discriminatedUnion("op", [
      CanvasInteractOp,
      CanvasPlaceOp,
      CanvasTidyOp,
      CanvasClearOp.extend({ block_ids: z.array(CanvasBlockId).min(1).max(16) }),
    ]),
  })
  .meta({ id: "CanvasInteractRequest" });

/** `run.step` frame data: a step of a run the viewer may view, without its payload. */
export const LiveRunStep = z
  .object({
    run_id: Uuid,
    seq: z.number().int(),
    kind: z.string(),
    /** The tool a `tool_call` step called, when the step says (never for agent-only steps). */
    tool: z.string().nullable(),
    /** The step's one-line summary, as the run's page shows it. */
    summary: z.string().nullable(),
    model: z.string().nullable(),
    duration_ms: z.number().int().nullable(),
    at: Timestamp,
  })
  .meta({ id: "LiveRunStep" });

/** `canvas.op` frame data: the op as this viewer may see it (a show keeps visible blocks only). */
export const LiveCanvasOp = z
  .object({ id: Uuid, session_id: Uuid.nullable(), op: CanvasOp, at: Timestamp })
  .meta({ id: "LiveCanvasOp" });

/** `task.changed` and `helper.commitment` frames carry the change ping (RT-01) as is. */
export const LiveChangeFrame = ChangePing;

/** CHAIN-VIEW (A16): a station of a request's chain, and how far along it is. */
export const ChainStatus = z
  .enum(["waiting", "working", "done", "failed"])
  .meta({ id: "ChainStatus" });
export type ChainStatus = z.infer<typeof ChainStatus>;

export const ChainNode = z
  .object({
    /** Stable per station: `requester`, `de`, `delivery`, or the helper card's id. */
    id: z.string(),
    role: z.enum(["requester", "de", "helper", "delivery"]),
    label: z.string(),
    status: ChainStatus,
    /** The station's step in plain words ("slides rendering"), when it has one. */
    detail: z.string().nullable(),
    /** What the station produced (a helper card's result), opened on click; null: nothing. */
    output: z.string().nullable(),
    /** No output: who the work moved on to, and the ask it carried (rendered "no output, handed to X with this ask"). */
    handed_to: z.string().nullable(),
    ask: z.string().nullable(),
    at: Timestamp.nullable(),
  })
  .meta({ id: "ChainNode" });
export type ChainNode = z.infer<typeof ChainNode>;

/** A task's request chain, as the viewer may read it: requester → DE → helpers → delivery. */
export const ChainView = z
  .object({
    task_id: Uuid,
    /** null: no request of a DE is recorded on the task. */
    chain: z
      .object({
        id: Uuid,
        title: z.string(),
        state: z.string(),
        step: z.string().nullable(),
        updated_at: Timestamp,
        nodes: z.array(ChainNode),
      })
      .nullable(),
  })
  .meta({ id: "ChainView" });
export type ChainView = z.infer<typeof ChainView>;

export const LiveChainQuery = z.object({ task_id: Uuid });

export const canvasRoutes = {
  "liveCanvas.get": defineRoute({
    feature: "core",
    id: "liveCanvas.get",
    method: "get",
    path: `${base}/live/canvas`,
    tag: "live",
    summary: "The caller's Live canvas (blocks it may see), and the stream's resume cursor",
    description:
      "The fold of the canvas ops of this session and the company-wide ones, each block " +
      "re-checked against the caller (a block it may not read is left out). Without " +
      "`session_id` a new session id is minted. 404 while the company's `live_canvas` switch " +
      "is off. Polled as the fallback when the live stream is down.",
    access: "viewer",
    params: CompanyParams,
    query: LiveCanvasQuery,
    response: { status: 200, schema: CanvasState },
    errors: [],
    emits: [],
  }),
  "liveCanvas.events": defineRoute({
    feature: "core",
    id: "liveCanvas.events",
    method: "get",
    path: `${base}/live/canvas/events`,
    tag: "live",
    summary:
      "The Live session's channel (server-sent events): steps, task and helper changes, canvas ops",
    description:
      "An SSE stream: `ready`; `run.step` (LiveRunStep) for runs the caller may view; " +
      "`task.changed` and `helper.commitment` (ChangePing) for tasks and commitments it may " +
      "read; `canvas.op` (LiveCanvasOp, a show keeping only the blocks it may see) for its " +
      "session and the company-wide ops. Every frame's `id` is the resume point: a reconnect " +
      "with `Last-Event-ID` (or `last_event_id`) first replays what it missed (canvas ops and " +
      "steps of the last 10 minutes, pings by seq), then goes live. `resync` when frames may " +
      "have been lost; `end` with `{reason}` before the server closes. Access is re-checked " +
      "at every batch and keep-alive.",
    access: "viewer",
    params: CompanyParams,
    query: LiveCanvasEventsQuery,
    response: { status: 200, contentType: "text/event-stream" },
    errors: [],
    emits: [],
  }),
  "liveCanvas.interact": defineRoute({
    feature: "core",
    id: "liveCanvas.interact",
    method: "post",
    path: `${base}/live/canvas/interact`,
    tag: "live",
    summary: "Record what the caller did on its Live page (opened a card, played a video)",
    description:
      "Appends an `interact` op to the caller's Live session, so the agent reads the person's " +
      "clicks as context (MCP `show_user` `op: context`). A block it names must be one the " +
      "caller may see. 404 while the company's `live_canvas` switch is off.",
    access: "viewer",
    params: CompanyParams,
    body: CanvasInteractRequest,
    response: { status: 201, schema: CanvasOpResult },
    errors: [],
    emits: ["live.interacted"],
  }),
  "liveCanvas.chain": defineRoute({
    feature: "core",
    id: "liveCanvas.chain",
    method: "get",
    path: `${base}/live/canvas/chain`,
    tag: "live",
    summary: "A task's request chain for the canvas's chain block",
    description:
      "The helper registry, cards and handoffs of the task's latest request as stations " +
      "(requester, the DE, one per helper card, delivery) with status and output. Served only " +
      "to a viewer who may read the task (404 otherwise); 404 while `live_canvas` is off. The " +
      "block refetches it when `task.changed` / `helper.commitment` frames arrive.",
    access: "viewer",
    params: CompanyParams,
    query: LiveChainQuery,
    response: { status: 200, schema: ChainView },
    errors: [],
    emits: [],
  }),
  // The ops are written by MCP `show_user` (services/api/src/mcp/canvas.ts, in the MCP session's
  // own tenant transaction) and by the voice service through the same tool; no HTTP write route.
} as const;
